import { Alert } from '@synoptic/ui-kit/alert/alert.js';
import { Button } from '@synoptic/ui-kit/button/button.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@synoptic/ui-kit/collapsible/collapsible.tsx';
import { SoundOffIcon } from '@synoptic/ui-kit/icons/react/sound-off.tsx';
import {
  ModalContent,
  ModalHeader,
  ModalRoot,
  ModalTitle,
} from '@synoptic/ui-kit/modal/modal.js';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from '@synoptic/ui-kit/select/select.js';
import { Separator } from '@synoptic/ui-kit/separator/separator.js';
import { Slider } from '@synoptic/ui-kit/slider/slider.js';
import { Switch } from '@synoptic/ui-kit/switch/switch.tsx';
import { FC, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { CustomCollection } from '@/__generated__/schema-types.ts';
import {
  SoundTrack,
  DEFAULT_SOUND_TRACK,
  DEFAULT_SOUND_VOLUME,
  DEFAULT_HIGHLIGHT_SOUND_TRACK,
  DEFAULT_MATCH_SOUND_TRACK,
  CollectionSoundSettings,
  HighlightSoundSettings,
  SOUND_TRACK_LABELS,
  isReadAloudTrack,
} from '@/components/toggle-sound/sound-settings';
import { SoundStatus } from '@/components/toggle-sound/sound-status.ts';
import { playNotificationAudio } from '@/utils/audio-utils/play-notification-audio';
import { isReadAloudSupported } from '@/utils/audio-utils/read-aloud.ts';
import {
  settingsContainer,
  settingGroup,
  settingLabel,
  switchLabel,
  switchHelpText,
  highlightSoundSection,
  expandableSwitchGroupLabel,
} from './sound-settings-modal.css';

export type BaseSoundSettings = {
  track: SoundTrack;
  volume: number;
};

type SoundTrackItem = { value: string; content: string };

type PlayPreview = (track: SoundTrack, volume: number) => void;

type CategorySoundSectionProps = {
  enableLabel: string;
  trackLabel: string;
  category: HighlightSoundSettings;
  soundTrackItems: SoundTrackItem[];
  playPreview: PlayPreview;
  onEnabledChange?: (enabled: boolean) => void;
  onTrackChange?: (track: SoundTrack) => void;
  onVolumeChange?: (volume: number) => void;
};

const CategorySoundSection: FC<CategorySoundSectionProps> = ({
  enableLabel,
  trackLabel,
  category,
  soundTrackItems,
  playPreview,
  onEnabledChange,
  onTrackChange,
  onVolumeChange,
}) => {
  const [localVolume, setLocalVolume] = useState(category.volume);

  const debouncedVolumeChange = useDebouncedCallback((volume: number) => {
    onVolumeChange?.(volume);
  }, 200);

  return (
    <Collapsible open={category.enabled}>
      <CollapsibleTrigger asChild>
        <label className={expandableSwitchGroupLabel}>
          {enableLabel}
          <Switch
            checked={category.enabled}
            onCheckedChange={onEnabledChange}
          />
        </label>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className={highlightSoundSection}>
          <div className={settingGroup}>
            <label className={settingLabel}>{trackLabel}</label>
            <Select
              value={category.track}
              onValueChange={(value) => {
                onTrackChange?.(value as SoundTrack);
                playPreview(value as SoundTrack, localVolume);
              }}
            >
              <SelectTrigger
                triggerText={SOUND_TRACK_LABELS[category.track]}
                placeholder="Select sound"
              />
              <SelectContent items={soundTrackItems} />
            </Select>
          </div>

          <div className={settingGroup}>
            <label className={settingLabel}>
              Volume: {Math.round(localVolume * 100)}%
            </label>
            <Slider
              value={[localVolume]}
              onValueChange={([volume]) => {
                setLocalVolume(volume);
                debouncedVolumeChange(volume);
              }}
              onValueCommit={([volume]) => playPreview(category.track, volume)}
              min={0}
              max={1}
              step={0.01}
              withLabels={false}
            />
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export type SoundSettingsModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  collection?: CustomCollection;
  settings?: BaseSoundSettings;
  soundStatus?: SoundStatus;
  showHighlightSettings?: boolean;
  showMatchSettings?: boolean;
  onToggleSound?: () => void;
  onTrackChange?: (track: SoundTrack) => void;
  onVolumeChange?: (volume: number) => void;
  onHighlightSoundEnabledChange?: (enabled: boolean) => void;
  onHighlightTrackChange?: (track: SoundTrack) => void;
  onHighlightVolumeChange?: (volume: number) => void;
  onMuteOtherPostsChange?: (muteOtherPosts: boolean) => void;
  onPlayHighlightsChange?: (playHighlights: boolean) => void;
  onMatchSoundEnabledChange?: (enabled: boolean) => void;
  onMatchTrackChange?: (track: SoundTrack) => void;
  onMatchVolumeChange?: (volume: number) => void;
  onPlayMatchesChange?: (playMatches: boolean) => void;
};

export const SoundSettingsModal: FC<SoundSettingsModalProps> = ({
  open,
  onOpenChange,
  collection,
  settings,
  soundStatus,
  showHighlightSettings = true,
  showMatchSettings = false,
  onToggleSound,
  onTrackChange,
  onVolumeChange,
  onHighlightSoundEnabledChange,
  onHighlightTrackChange,
  onHighlightVolumeChange,
  onMuteOtherPostsChange,
  onPlayHighlightsChange,
  onMatchSoundEnabledChange,
  onMatchTrackChange,
  onMatchVolumeChange,
  onPlayMatchesChange,
}) => {
  const currentTrack = settings?.track || DEFAULT_SOUND_TRACK;
  const currentVolume = settings?.volume ?? DEFAULT_SOUND_VOLUME;

  // Callers outside the terminal pass a bare track/volume pair
  const collectionSettings =
    settings && 'highlightSound' in settings
      ? (settings as CollectionSoundSettings)
      : undefined;

  const highlightSound: HighlightSoundSettings = {
    enabled: collectionSettings?.highlightSound?.enabled ?? false,
    track:
      collectionSettings?.highlightSound?.track ??
      DEFAULT_HIGHLIGHT_SOUND_TRACK,
    volume: collectionSettings?.highlightSound?.volume ?? DEFAULT_SOUND_VOLUME,
  };
  const matchSound: HighlightSoundSettings = {
    enabled: collectionSettings?.matchSound?.enabled ?? false,
    track: collectionSettings?.matchSound?.track ?? DEFAULT_MATCH_SOUND_TRACK,
    volume: collectionSettings?.matchSound?.volume ?? DEFAULT_SOUND_VOLUME,
  };

  const hasHighlights =
    showHighlightSettings &&
    collection?.highlights?.isEnabled &&
    collection?.highlights?.values &&
    collection.highlights.values.length > 0;

  const playHighlights = collectionSettings?.playHighlights ?? true;
  const playMatches = collectionSettings?.playMatches ?? true;

  // Only a category this modal offers counts, or narrowing could leave a feed
  // silent with no switch here to widen it again.
  const keepsHighlights = Boolean(hasHighlights) && playHighlights;
  const keepsMatches = Boolean(showMatchSettings) && playMatches;

  const playsForEveryPost = !(
    (collectionSettings?.muteOtherPosts ?? false) &&
    (keepsHighlights || keepsMatches)
  );

  const handleEveryPostChange = (everyPost: boolean) => {
    if (!everyPost && !keepsHighlights && !keepsMatches) {
      if (hasHighlights) onPlayHighlightsChange?.(true);
      if (showMatchSettings) onPlayMatchesChange?.(true);
    }
    onMuteOtherPostsChange?.(!everyPost);
  };

  const handlePlayHighlightsChange = (next: boolean) => {
    onPlayHighlightsChange?.(next);
    if (!next && !keepsMatches) onMuteOtherPostsChange?.(false);
  };

  const handlePlayMatchesChange = (next: boolean) => {
    onPlayMatchesChange?.(next);
    if (!next && !keepsHighlights) onMuteOtherPostsChange?.(false);
  };

  const isSoundOff = soundStatus === SoundStatus.off;

  const [localVolume, setLocalVolume] = useState(currentVolume);

  const debouncedVolumeChange = useDebouncedCallback((volume: number) => {
    onVolumeChange?.(volume);
  }, 200);

  const handleVolumeChange = (values: number[]) => {
    const newVolume = values[0];
    setLocalVolume(newVolume);
    debouncedVolumeChange(newVolume);
  };

  const soundTrackItems = Object.entries(SOUND_TRACK_LABELS)
    .filter(
      ([value]) =>
        !isReadAloudTrack(value as SoundTrack) || isReadAloudSupported(),
    )
    .map(([value, label]) => ({ value, content: label }));

  const playPreview: PlayPreview = (track, volume) => {
    void playNotificationAudio({
      track,
      volume,
      text: 'New posts will be read aloud like this.',
      ownerId: 'sound-settings-preview',
      interruptSpeech: true,
    }).catch(() => {});
  };

  const handleTrackChange = (track: SoundTrack) => {
    onTrackChange?.(track);
    playPreview(track, localVolume);
  };

  const handleVolumeCommit = ([volume]: number[]) => {
    playPreview(currentTrack, volume);
  };

  return (
    <ModalRoot open={open} onOpenChange={onOpenChange}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Sound settings</ModalTitle>
        </ModalHeader>

        <div className={settingsContainer}>
          {isSoundOff && (
            <Alert
              icon={<SoundOffIcon />}
              primaryAction={
                <Button
                  size="small"
                  variant="secondary"
                  onClick={onToggleSound}
                >
                  Enable sound
                </Button>
              }
            >
              Sound is currently off{collection ? ' for this collection' : ''}.
            </Alert>
          )}

          <div className={settingGroup}>
            <label className={settingLabel}>Sound track</label>
            <Select
              value={currentTrack}
              onValueChange={(value) => handleTrackChange(value as SoundTrack)}
            >
              <SelectTrigger
                triggerText={SOUND_TRACK_LABELS[currentTrack]}
                placeholder="Select sound"
              />
              <SelectContent items={soundTrackItems} />
            </Select>
          </div>

          <div className={settingGroup}>
            <label className={settingLabel}>
              Volume: {Math.round(localVolume * 100)}%
            </label>
            <Slider
              value={[localVolume]}
              onValueChange={handleVolumeChange}
              onValueCommit={handleVolumeCommit}
              min={0}
              max={1}
              step={0.01}
              withLabels={false}
            />
          </div>

          {(hasHighlights || showMatchSettings) && (
            <Collapsible open={!playsForEveryPost}>
              <CollapsibleTrigger asChild>
                <label className={expandableSwitchGroupLabel}>
                  <span className={switchLabel}>
                    Play for every post
                    {!playsForEveryPost && (
                      <span className={switchHelpText}>
                        Pick below which posts play a sound.
                      </span>
                    )}
                  </span>
                  <Switch
                    checked={playsForEveryPost}
                    onCheckedChange={handleEveryPostChange}
                  />
                </label>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className={highlightSoundSection}>
                  {hasHighlights && (
                    <label className={expandableSwitchGroupLabel}>
                      <span className={switchLabel}>
                        Posts with a highlight
                      </span>
                      <Switch
                        checked={playHighlights}
                        onCheckedChange={handlePlayHighlightsChange}
                      />
                    </label>
                  )}
                  {showMatchSettings && (
                    <label className={expandableSwitchGroupLabel}>
                      <span className={switchLabel}>
                        Posts with a matched market
                      </span>
                      <Switch
                        checked={playMatches}
                        onCheckedChange={handlePlayMatchesChange}
                      />
                    </label>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {hasHighlights && (
            <>
              <Separator />
              <CategorySoundSection
                enableLabel="Enable different sound for highlights"
                trackLabel="Highlight sound track"
                category={highlightSound}
                soundTrackItems={soundTrackItems}
                playPreview={playPreview}
                onEnabledChange={onHighlightSoundEnabledChange}
                onTrackChange={onHighlightTrackChange}
                onVolumeChange={onHighlightVolumeChange}
              />
            </>
          )}

          {showMatchSettings && (
            <>
              <Separator />
              <CategorySoundSection
                enableLabel="Enable different sound for matched markets"
                trackLabel="Matched market sound track"
                category={matchSound}
                soundTrackItems={soundTrackItems}
                playPreview={playPreview}
                onEnabledChange={onMatchSoundEnabledChange}
                onTrackChange={onMatchTrackChange}
                onVolumeChange={onMatchVolumeChange}
              />
            </>
          )}
        </div>
      </ModalContent>
    </ModalRoot>
  );
};
